import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { UserCredentialsService } from './user-credentials.service';
import { UserAccountService } from './user-account.service';
import { CookieService } from 'ngx-cookie-service';
import { Constants } from './util/constants';

export interface ImpersonationInfo {
  isImpersonated: boolean;
  agentEmail: string;
  impersonatedUser: string;
  expiryTime: number;
  permissions: string;
  impToken: string;
  requestId: string;
  // Agent session state
  agentSessionState?: 'READ_ONLY' | 'PENDING' | 'AUTHORIZED' | 'EXPIRED';
  upgradeRequestId?: string;
  intent?: string;
  pendingStartTime?: number; // Timestamp when PENDING state started
}

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private impersonationSubject = new BehaviorSubject<ImpersonationInfo | null>(null);
  public impersonation$ = this.impersonationSubject.asObservable();
  private countdownInterval: any;
  private originalUserToken: string | null = null;
  private lastValidationTime = 0;
  private validationCooldown = 30000; // 30 seconds between validations

  constructor(
    private userCredentials: UserCredentialsService,
    private userAccountService: UserAccountService,
    private http: HttpClient,
    private cookieService: CookieService
  ) {
    // Try to restore impersonation state from localStorage on service initialization
    this.restoreImpersonationFromStorage();

    // Listen for logout events to clear impersonation state
    this.setupLogoutListener();
  }

  /**
   * Setup listener for logout events to clear impersonation state
   */
  private setupLogoutListener() {
    // Listen for custom logout event
    window.addEventListener('user-logged-out', () => {
      console.log('Received logout event, clearing impersonation state');
      this.endImpersonation();
    });

    // Check periodically if localStorage was cleared externally
    setInterval(() => {
      const currentInfo = this.getCurrentImpersonation();
      if (currentInfo) {
        const stored = localStorage.getItem('impersonation_info');
        if (!stored) {
          console.log('Impersonation localStorage was cleared externally, clearing service state');
          this.impersonationSubject.next(null);
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
          }
        }
        // No longer doing periodic token validation - validation only happens during restoration
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Detects impersonation mode from URL impToken parameter
   */
  detectImpersonationFromUrl(impToken: string): boolean {
    if (!impToken || impToken.trim().length === 0) {
      return false;
    }

    try {
      // Decode JWT token to extract impersonation details
      const decoded = this.decodeJWTToken(impToken);
      if (decoded && decoded.type === 'AGENT_IMPERSONATION') {
        const expiryTime = decoded.expires || decoded.exp;

        // Preserve existing agent session state from localStorage if available
        const existingInfo = this.getCurrentImpersonation();

        const impInfo: ImpersonationInfo = {
          isImpersonated: true,
          agentEmail: decoded.agentId,
          impersonatedUser: decoded.userId,
          expiryTime: expiryTime,
          permissions: decoded.permissions,
          impToken: impToken,
          requestId: decoded.requestId,
          // Preserve agent session state fields if they exist
          agentSessionState: existingInfo?.agentSessionState,
          upgradeRequestId: existingInfo?.upgradeRequestId,
          intent: existingInfo?.intent,
          pendingStartTime: existingInfo?.pendingStartTime
        };

        this.impersonationSubject.next(impInfo);
        this.startExpiryCountdown(expiryTime);

        // Store original user token before replacing with impersonation token
        this.originalUserToken = this.userCredentials.getSToken() || this.userAccountService.getSToken();

        // Update both token services so all API calls use the impersonation token
        this.userCredentials.setSToken(impToken);
        this.userAccountService.setSToken(impToken);
        console.log('Set initial impersonation token in both UserCredentialsService and UserAccountService');

        // Persist to localStorage to survive page refresh
        this.saveImpersonationToStorage(impInfo);

        return true;
      }
    } catch (error) {
      console.error('Failed to decode impersonation token:', error);
    }

    return false;
  }

  /**
   * Decode JWT token (Base64 decode + JSON parse)
   */
  private decodeJWTToken(token: string): any {
    try {
      const decoded = atob(token);
      const tokenObj = JSON.parse(decoded);

      // New token format from refreshImpersonationToken has payload wrapper
      if (tokenObj.payload && typeof tokenObj.payload === 'object') {
        return tokenObj.payload;
      }

      // If no payload wrapper, token might be malformed
      console.error('Token missing expected payload structure:', tokenObj);
      return null;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Get current impersonation info
   */
  getCurrentImpersonation(): ImpersonationInfo | null {
    return this.impersonationSubject.value;
  }

  /**
   * Check if currently impersonated
   */
  isCurrentlyImpersonated(): boolean {
    const info = this.getCurrentImpersonation();
    return info !== null && info.isImpersonated;
  }

  /**
   * Get impToken for API calls
   */
  getImpToken(): string | null {
    const info = this.getCurrentImpersonation();
    return info ? info.impToken : null;
  }

  /**
   * Get impersonation token (alias for getImpToken)
   */
  getImpersonationToken(): string | null {
    return this.getImpToken();
  }

  /**
   * Update impersonation token after permission upgrade
   */
  updateImpersonationToken(newToken: string): boolean {
    try {
      const decoded = this.decodeJWTToken(newToken);
      if (decoded && decoded.type === 'AGENT_IMPERSONATION') {
        const currentInfo = this.getCurrentImpersonation();
        if (currentInfo) {
          const expiryTime = decoded.expires || decoded.exp;

          const updatedInfo: ImpersonationInfo = {
            ...currentInfo,
            impToken: newToken,
            permissions: decoded.permissions,
            expiryTime: expiryTime,
            requestId: decoded.requestId
          };

          this.impersonationSubject.next(updatedInfo);
          this.startExpiryCountdown(expiryTime);

          // Update both token services so all API calls use the new token
          this.userCredentials.setSToken(newToken);
          this.userAccountService.setSToken(newToken);
          console.log('Updated both UserCredentialsService and UserAccountService with new impersonation token');

          // Persist updated token to localStorage
          this.saveImpersonationToStorage(updatedInfo);

          return true;
        }
      }
    } catch (error) {
      console.error('Failed to update impersonation token:', error);
    }
    return false;
  }

  /**
   * Refresh token after permission upgrade
   */
  async refreshTokenAfterUpgrade(): Promise<boolean> {
    try {
      const currentInfo = this.getCurrentImpersonation();
      if (!currentInfo) {
        console.error('No current impersonation to refresh');
        return false;
      }

      // Call backend to get new token with updated permissions
      const response = await fetch(environment.impersonationRefreshTokenURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentInfo.impersonatedUser}:${currentInfo.impToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data?.newToken) {
          // Update token in service
          const success = this.updateImpersonationToken(data.data.newToken);
          if (success) {
            console.log('Token refreshed successfully after permission upgrade');
            return true;
          }
        } else {
          console.warn('Token refresh response was not successful:', data);
        }
      } else if (response.status === 403) {
        console.error('Token refresh failed - impersonation session is no longer valid (403 Forbidden)');
        this.endImpersonation();
        this.promptSessionExpired();
        return false;
      } else {
        console.error('Token refresh failed with status:', response.status);
      }

      // Fallback: Prompt for page refresh
      console.warn('Token refresh failed. Page reload may be required.');
      this.promptTokenRefresh();
      return false;

    } catch (error) {
      console.error('Error refreshing token:', error);
      this.promptTokenRefresh();
      return false;
    }
  }

  /**
   * Handle token refresh failure silently (no user prompt needed for OTA)
   */
  private promptTokenRefresh(): void {
    // Silently handle token refresh failure without user prompt
    console.log('Token permissions updated, continuing with existing session');
    // No page reload needed - let the application continue normally
  }

  /**
   * Prompt user when impersonation session has expired
   */
  private promptSessionExpired(): void {
    alert('Your impersonation session has expired. Please start a new impersonation session.');
  }

  /**
   * Check if token needs refresh based on API error
   */
  shouldRefreshToken(error: any): boolean {
    return error?.status === 403 &&
           this.isCurrentlyImpersonated() &&
           (error?.error?.message?.includes('permission') ||
            error?.error?.message?.includes('token'));
  }

  /**
   * Check if current token is expired
   */
  isTokenExpired(): boolean {
    const info = this.getCurrentImpersonation();
    if (!info) return false;

    return Date.now() >= info.expiryTime;
  }

  /**
   * Check if current token is near expiry (within 5 minutes)
   */
  isTokenNearExpiry(): boolean {
    const info = this.getCurrentImpersonation();
    if (!info) return false;

    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return fiveMinutesFromNow >= info.expiryTime;
  }

  /**
   * Automatically refresh token if expired or near expiry
   */
  async autoRefreshIfNeeded(): Promise<boolean> {
    if (!this.isCurrentlyImpersonated()) {
      return true; // No impersonation, nothing to refresh
    }

    if (this.isTokenExpired()) {
      console.warn('Impersonation token has expired, attempting refresh...');
      return await this.refreshTokenAfterUpgrade();
    }

    if (this.isTokenNearExpiry()) {
      console.log('Impersonation token is near expiry, proactively refreshing...');
      return await this.refreshTokenAfterUpgrade();
    }

    return true; // Token is still valid
  }

  /**
   * Get time remaining until expiry (in minutes)
   */
  getTimeRemainingMinutes(): number {
    const info = this.getCurrentImpersonation();
    if (!info) return 0;

    const now = Date.now();
    const remaining = Math.max(0, info.expiryTime - now);
    return Math.floor(remaining / (1000 * 60));
  }

  /**
   * Start countdown timer for session expiry
   */
  private startExpiryCountdown(expiryTime: number) {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, expiryTime - now);

      if (remaining <= 0) {
        console.log('Impersonation token has expired, clearing state');
        this.endImpersonation();
      }
    }, 5000); // Check every 5 seconds for more responsive expiration detection
  }

  /**
   * End impersonation session
   */
  endImpersonation() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Restore original user token when impersonation ends
    if (this.originalUserToken) {
      this.userCredentials.setSToken(this.originalUserToken);
      this.userAccountService.setSToken(this.originalUserToken);
      console.log('Restored original user token in both services after impersonation ended');
      this.originalUserToken = null;
    }

    this.impersonationSubject.next(null);

    // Clear localStorage
    this.clearImpersonationFromStorage();
  }

  /**
   * Clear impersonation on logout
   */
  clearImpersonation() {
    this.endImpersonation();
  }

  /**
   * Called when user logs out to ensure impersonation state is cleared
   */
  onUserLogout() {
    console.log('User logged out, clearing impersonation state');
    this.endImpersonation();
  }

  /**
   * Validate impersonation state after user session is loaded
   * Call this when you're sure the user session is initialized
   */
  validateImpersonationAfterLogin() {
    const currentInfo = this.getCurrentImpersonation();

    if (currentInfo && !this.userAccountService.isLoggedIn()) {
      console.log('User not logged in but impersonation state exists, clearing it');
      this.endImpersonation();
    }
  }

  /**
   * Check if impersonation banner should be visible
   * Banner should only show if there's valid impersonation state
   * We don't check isLoggedIn() because impersonation itself indicates an active agent session
   */
  shouldShowImpersonationBanner(): boolean {
    const currentInfo = this.getCurrentImpersonation();
    if (!currentInfo || !currentInfo.isImpersonated) {
      return false;
    }

    // Check if impersonation token is still valid (not expired)
    if (Date.now() >= currentInfo.expiryTime) {
      console.log('Impersonation token has expired, hiding banner');
      this.endImpersonation();
      return false;
    }

    // Check if we have a valid impersonation token
    const impToken = this.getImpToken();
    if (!impToken) {
      console.log('No impersonation token available, hiding banner');
      return false;
    }

    // Token validation is now done during restoration only
    // No need for frequent validation that could clear state prematurely

    return true;
  }

  /**
   * Asynchronously validate token using /verifyAuth endpoint
   */
  private async validateTokenAsync(): Promise<void> {
    try {
      const isValid = await this.verifyTokenWithBackend();
      if (!isValid) {
        console.log('Token validation failed with /verifyAuth endpoint - but allowing natural expiry');
        // Don't immediately clear - let natural expiry timer handle it
        // This prevents premature clearing due to transient issues
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Don't clear on validation errors - let natural expiry handle cleanup
      console.log('Token validation threw error - allowing natural expiry to handle cleanup');
    }
  }

  /**
   * Verify token validity using /verifyAuth endpoint
   */
  private async verifyTokenWithBackend(): Promise<boolean> {
    const currentInfo = this.getCurrentImpersonation();
    if (!currentInfo) {
      return false;
    }

    try {
      // Use same format as UserAccountService - GET method with Authorization header
      const headers = new HttpHeaders()
        .set('Authorization', `Bearer ${currentInfo.impersonatedUser}:${currentInfo.impToken}`)
        .set(Constants.PARAM_RS_CLIENT_COOKIE, this.cookieService.get('RS-CLIENT'));

      const response = await this.http.get(environment.apiForCredentialsVerfication, { headers }).toPromise();

      // Check if response indicates success
      return response && (response as any).status === 'success';

    } catch (error: any) {
      console.error('Token verification error:', error);

      // Only treat as definitively invalid for specific error codes
      if (error.status === 401 || error.status === 403) {
        console.log(`Token verification failed with status ${error.status} - token is expired/invalid`);
        return false;
      }

      // For other errors (network issues, etc.), assume token is still valid
      // This prevents clearing state due to temporary connectivity issues
      console.warn(`Token verification failed with status ${error.status} - assuming token is still valid`);
      return true;
    }
  }

  /**
   * Update agent session state in current impersonation
   */
  updateAgentSessionState(state: 'READ_ONLY' | 'PENDING' | 'AUTHORIZED' | 'EXPIRED', upgradeRequestId?: string, intent?: string): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        agentSessionState: state,
        upgradeRequestId: upgradeRequestId || currentInfo.upgradeRequestId,
        intent: intent || currentInfo.intent,
        // Store timestamp when entering PENDING state
        pendingStartTime: state === 'PENDING' ? Date.now() : currentInfo.pendingStartTime
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveImpersonationToStorage(updatedInfo);
      console.log('Updated agent session state to:', state);
    }
  }

  /**
   * Save impersonation info to localStorage
   */
  private saveImpersonationToStorage(impInfo: ImpersonationInfo): void {
    try {
      localStorage.setItem('impersonation_info', JSON.stringify(impInfo));
      console.log('Saved impersonation info to localStorage');
    } catch (error) {
      console.error('Failed to save impersonation info to localStorage:', error);
    }
  }

  /**
   * Restore impersonation info from localStorage
   */
  private async restoreImpersonationFromStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem('impersonation_info');
      if (stored) {
        const impInfo: ImpersonationInfo = JSON.parse(stored);

        // Check if token is still valid (not expired based on local time)
        if (Date.now() < impInfo.expiryTime) {
          // Temporarily restore state to enable verifyAuth call
          this.impersonationSubject.next(impInfo);
          this.userCredentials.setSToken(impInfo.impToken);
          this.userAccountService.setSToken(impInfo.impToken);

          console.log('Verifying stored impersonation token with backend...');

          // Verify with backend during restoration
          const isValid = await this.verifyTokenWithBackend();

          if (isValid) {
            // Token is valid, keep the restored state and start countdown
            this.startExpiryCountdown(impInfo.expiryTime);
            console.log('✓ Restored impersonation state from localStorage with permissions:', impInfo.permissions);
          } else {
            // Token validation failed, but don't clear immediately
            // Let the natural expiry timer handle cleanup if token is truly expired
            console.warn('⚠ Stored impersonation token validation failed, but keeping state - will expire naturally if invalid');
            this.startExpiryCountdown(impInfo.expiryTime);
          }
        } else {
          console.log('Stored impersonation token has expired, clearing storage');
          this.clearImpersonationFromStorage();
        }
      }
    } catch (error) {
      console.error('Failed to restore impersonation from localStorage:', error);
      this.clearImpersonationFromStorage();
    }
  }

  /**
   * Clear impersonation info from localStorage
   */
  private clearImpersonationFromStorage(): void {
    try {
      localStorage.removeItem('impersonation_info');
      console.log('Cleared impersonation info from localStorage');
    } catch (error) {
      console.error('Failed to clear impersonation info from localStorage:', error);
    }
  }
}